import { Deck, DeckCard, DeckCardTagType, DeckFileFormat } from "@apiserver-rest/ponder-data-client-base";

const exporterMap = new Map<DeckFileFormat, Function>([
    [DeckFileFormat.Wizards, exportWizards],
    [DeckFileFormat.Moxfield, exportMoxfield],
    [DeckFileFormat.MtgWtf, exportMtgWtf]
]);

const sideboardExporterMap = new Map<DeckFileFormat, boolean>([
    [DeckFileFormat.Wizards, false],
    [DeckFileFormat.Moxfield, true],
    [DeckFileFormat.MtgWtf, true]
]);

export function exportDeck(deck: Deck, format: DeckFileFormat, exportSideboard: boolean): string | undefined {
    if (exporterMap.has(format)) {
        var f = exporterMap.get(format);
        
        if (f) {
            return f(deck, exportSideboard);
        }
    }
        
    return "Error!"
}

export function canExportSideboard(format: DeckFileFormat): boolean {
    return sideboardExporterMap.get(format) ?? false;
}

/**
 * Format for decks on the official Wizards of the Coast website. 
 * 
 *     {count} {name}
 * 
 * Example:
 * 
 *     1 Disa the Restless
 *     1 Coram, the Undertaker
 *     1 Find//Finality
 * 
 * Note the "Find//Finality" and not "Find // Finality".
 * 
 * @param deck
 * @returns 
 */
function exportWizards(deck: Deck, exportSideboard: boolean): string {
    var contents = "";
    var mainboard = deck.layers?.at(0);

    if (mainboard && mainboard.cards?.length) {
        mainboard.cards.forEach(c => {
            var name = c.card.item.name!.replaceAll(" // ", "//");
            contents += `${c.count + c.specialCount} ${name}\n`;
        });
    }

    return contents.trim();
}

/**
 * Format for decks on Moxfield.
 * 
 *     {count} {name} ({set code}) {set index} {foil flag}
 * 
 * Example:
 * 
 *     1 Disa the Restless (M3C) 1 *F*
 *     1 Accursed Marauder (MH3) 80
 *     1 Find // Finality (M3C) 261
 * 
 * @param deck 
 * @returns 
 */
function exportMoxfield(deck: Deck, exportSideboard: boolean): string {
    var contents = "";
    var mainboard = deck.layers?.at(0);
    var sideboard = deck.layers?.at(1);

    if (mainboard && mainboard.cards?.length) {
        mainboard.cards.forEach(c => {
            contents += toMoxfield(c);
        });
    }

    if (sideboard && sideboard.cards?.length && exportSideboard) {
        contents += "\nSIDEBOARD:\n";
        sideboard.cards.forEach(c => {
            contents += toMoxfield(c);
        });
    }

    return contents.trim();
}

function toMoxfield(c: DeckCard): string {
    return `${c.count + c.specialCount} ${c.card.item.name!} (${c.card.item.data!.magic!.set.code?.toUpperCase()}) ${c.card.item.data!.magic!.setIndex} ${c.specialCount > 0 ? "*F*" : ""}\n`;
}

/**
 * Format for decks on mtg.wtf.
 * 
 *     {count} {name} [{set code}|{set index}] {foil flag}
 * 
 * Example:
 * 
 *     COMMANDER: 1 Kardur, Doomscourge [SCD:5] [foil]
 *     1 Archfiend of Depravity [SCD:67]
 *     1 Deadly Tempest [SCD:75]
 * 
 * @param deck 
 * @returns 
 */
function exportMtgWtf(deck: Deck, exportSideboard: boolean): string {
    var contents = "";
    var mainboard = deck.layers?.at(0);
    var sideboard = deck.layers?.at(1);

    if (mainboard && mainboard.cards?.length) {
        mainboard.cards.forEach(c => {
            contents += toMtgWtf(c);
        });
    }

    if (sideboard && sideboard.cards?.length && exportSideboard) {
        contents += "\nSIDEBOARD:\n";
        sideboard.cards.forEach(c => {
            contents += toMtgWtf(c);
        });
    }

    return contents.trim();
}

function toMtgWtf(c: DeckCard): string {
    var str = `${c.count + c.specialCount} ${c.card.item.name!} [${c.card.item.data!.magic!.set.code?.toUpperCase()}:${c.card.item.data!.magic!.setIndex}] ${c.specialCount > 0 ? "[foil]" : ""}\n`;

    if (c.tag == DeckCardTagType.Commander) {
        str = `COMMANDER: ${str}`;
    }

    return str;
}